/*
 * Candles by Midnight — mega-menu: mobile drawer, two-level drill-down.
 * Desktop dropdowns are pure CSS (hover/focus). This only drives mobile.
 *
 * FIXED: every listener used to be registered INSIDE toggleDrawer(), which meant
 * (a) the accordions did nothing until the drawer had been opened at least once,
 * and (b) each toggle re-registered them, so after N toggles a single tap fired
 * N times and the panel flickered open/shut. Binding happens once, here.
 */
const CBM_NAV_MOBILE = 989;

if (!customElements.get('cbm-nav')) {
  customElements.define(
    'cbm-nav',
    class CbmNav extends HTMLElement {
      connectedCallback() {
        this.toggleBtn = this.querySelector('[data-nav-toggle]');
        if (this.toggleBtn) {
          this.toggleBtn.addEventListener('click', () => this.toggleDrawer(this.toggleBtn));
        }

        // The burger lives in the HEADER section, outside this element's
        // subtree, so querySelector cannot reach it. Header and mega-menu are
        // sibling sections in the same group; a document-level listener is the
        // only way to bridge them without duplicating the nav markup.
        this.headerToggleHandler = (e) => {
          const btn = e.target.closest && e.target.closest('[data-cbm-nav-toggle]');
          if (!btn) return;
          e.preventDefault();
          this.toggleDrawer(btn);
        };
        document.addEventListener('click', this.headerToggleHandler);

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        // Never leave the document unscrollable behind a torn-down drawer.
        document.documentElement.style.overflow = '';
        if (this.headerToggleHandler) {
          document.removeEventListener('click', this.headerToggleHandler);
        }
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
      }

      onClick = (e) => {
        const isMobile = window.innerWidth <= CBM_NAV_MOBILE;

        // The drawer is a fixed panel over a scrim, and the scrim IS this
        // element — so a click landing on `this` rather than on the panel is a
        // click outside the menu, which should close it.
        if (isMobile && e.target === this && this.hasAttribute('data-open')) {
          this.closeDrawer();
          return;
        }

        const back = e.target.closest('[data-acc-back]');
        if (back) {
          e.preventDefault();
          this.closeAllPanels();
          return;
        }

        const accBtn = e.target.closest('[data-acc]');
        if (accBtn && this.contains(accBtn)) {
          // Desktop opens on hover/focus; intercepting the click there would
          // fight the CSS and trap keyboard users in a half-open state.
          if (!isMobile) return;
          e.preventDefault();
          const item = accBtn.closest('.cbmnav__item');
          const wasOpen = item.hasAttribute('data-acc-open');
          this.closeAllPanels();
          if (!wasOpen) {
            item.setAttribute('data-acc-open', '');
            accBtn.setAttribute('aria-expanded', 'true');
          }
          return;
        }

        // Tapping a real link closes the whole drawer.
        if (e.target.closest('a') && isMobile) {
          this.closeDrawer();
        }
      };

      onKeydown = (e) => {
        if (e.key !== 'Escape') return;
        this.closeDrawer();
      };

      closeAllPanels() {
        this.querySelectorAll('.cbmnav__item[data-acc-open]').forEach((item) => {
          item.removeAttribute('data-acc-open');
          const btn = item.querySelector('[data-acc]');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }

      setToggleState(open) {
        const controls = [this.toggleBtn].concat(
          Array.from(document.querySelectorAll('[data-cbm-nav-toggle]'))
        );
        controls.forEach((c) => c && c.setAttribute('aria-expanded', String(open)));
      }

      toggleDrawer() {
        if (this.hasAttribute('data-open')) this.closeDrawer();
        else this.openDrawer();
      }

      openDrawer() {
        this.setAttribute('data-open', '');
        this.setToggleState(true);
        // The drawer scrolls independently; without this the page behind it
        // scrolls under the panel whenever the list is short.
        this.lockScroll(true);
      }

      closeDrawer() {
        this.removeAttribute('data-open');
        this.closeAllPanels();
        this.setToggleState(false);
        this.lockScroll(false);
      }

      lockScroll(on) {
        if (window.innerWidth > CBM_NAV_MOBILE) return;
        document.documentElement.style.overflow = on ? 'hidden' : '';
      }
    }
  );
}
